import { useState, useEffect } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { X, Loader2, Plus, Globe, CalendarDays } from 'lucide-react'
import { projectsApi, clientsApi, usersApi, quotesApi, getApiErrorMessage } from '@/services/api'
import { cn } from '@/lib/utils'
import { ClientForm } from '@/components/clients/ClientForm'
import type { Project, ProjectType, ProjectStatus, ModuleType, Client, User, Quote, QuoteLineItem } from '@/types'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProjectFormData {
  name: string
  clientId: string
  isEvent: boolean
  primaryModule: ModuleType | ''
  type: ProjectType
  status: ProjectStatus
  description: string
  startDate: string
  endDate: string
  eventStartDate: string
  eventEndDate: string
  budget: string
  managerId: string
  // Campi evento
  location: string
  isOnline: boolean
  // Campi ADV
  adPlatforms: string[]
  adPlatformOther: string
}

interface ProjectFormModalProps {
  project?: Project | undefined
  onClose: () => void
  onSuccess?: () => void
}

// Use Record<string, string> to avoid exactOptionalPropertyTypes issues
type FormErrors = Record<string, string>

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const EUR = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' })

const AD_PLATFORMS = [
  { value: 'GOOGLE', label: 'Google Ads' },
  { value: 'META', label: 'Meta (Facebook / Instagram)' },
  { value: 'TIKTOK', label: 'TikTok Ads' },
  { value: 'LINKEDIN', label: 'LinkedIn Ads' },
  { value: 'PROGRAMMATIC', label: 'Programmatic / Display' },
  { value: 'YOUTUBE', label: 'YouTube Ads' },
]

const EVENT_TYPES: ProjectType[] = [
  'EVENT_ORGANIZATION', 'FAIR_STAND', 'CONFERENCE', 'PRODUCT_LAUNCH',
  'GALA_DINNER', 'WORKSHOP_SEMINAR', 'CORPORATE_EVENT', 'PRESS_CONFERENCE', 'ROADSHOW',
]

const EVENT_TYPE_OPTIONS: { value: ProjectType; label: string }[] = [
  { value: 'EVENT_ORGANIZATION', label: 'Organizzazione Evento' },
  { value: 'FAIR_STAND', label: 'Fiera & Stand' },
  { value: 'CONFERENCE', label: 'Conferenza / Convegno' },
  { value: 'PRODUCT_LAUNCH', label: 'Lancio Prodotto' },
  { value: 'GALA_DINNER', label: 'Gala / Cena di Gala' },
  { value: 'WORKSHOP_SEMINAR', label: 'Workshop / Seminario' },
  { value: 'CORPORATE_EVENT', label: 'Evento Aziendale' },
  { value: 'PRESS_CONFERENCE', label: 'Conferenza Stampa' },
  { value: 'ROADSHOW', label: 'Roadshow' },
]

const MODULE_OPTIONS: { value: ModuleType; label: string; defaultType: ProjectType; types: { value: ProjectType; label: string }[] }[] = [
  { value: 'SOCIAL', label: 'Social Media Management', defaultType: 'SOCIAL_MANAGEMENT', types: [
    { value: 'SOCIAL_MANAGEMENT', label: 'Social Media Management' },
  ]},
  { value: 'ADV', label: 'Advertising & Campagne Paid', defaultType: 'PERFORMANCE_MARKETING', types: [
    { value: 'PERFORMANCE_MARKETING', label: 'Performance Marketing' },
    { value: 'AWARENESS_CAMPAIGN', label: 'Campagna Awareness' },
    { value: 'LEAD_GENERATION', label: 'Lead Generation' },
    { value: 'RETARGETING', label: 'Retargeting / Remarketing' },
    { value: 'INFLUENCER_MARKETING', label: 'Influencer Marketing' },
  ]},
  { value: 'DIGITAL', label: 'Digital Hub (Web, SEO, App, AI)', defaultType: 'WEBSITE', types: [
    { value: 'WEBSITE', label: 'Sito Web' },
    { value: 'ECOMMERCE', label: 'E-commerce' },
    { value: 'SEO', label: 'SEO & Posizionamento' },
    { value: 'APP_WEBAPP', label: 'App & Webapp' },
    { value: 'CRM', label: 'CRM' },
    { value: 'ERP', label: 'ERP' },
    { value: 'CHATBOT', label: 'Chatbot' },
    { value: 'CHATBOT_VOICE', label: 'Chatbot Vocale' },
    { value: 'CHATBOT_AVATAR', label: 'Chatbot Avatar Animato' },
    { value: 'AUTOMATION', label: 'Automazione' },
  ]},
  { value: 'PR', label: 'Pubbliche Relazioni & Ufficio Stampa', defaultType: 'PRESS_OFFICE', types: [
    { value: 'PRESS_OFFICE', label: 'Pubbliche Relazioni' },
  ]},
  { value: 'BRANDING', label: 'Branding & Identità Visiva', defaultType: 'BRAND_IDENTITY', types: [
    { value: 'BRAND_IDENTITY', label: 'Brand Identity' },
    { value: 'REBRANDING', label: 'Rebranding' },
    { value: 'NAMING', label: 'Naming' },
    { value: 'PACKAGING_DESIGN', label: 'Packaging Design' },
  ]},
  { value: 'CONSULENZA', label: 'Consulenza Strategica', defaultType: 'STRATEGIC_CONSULTING', types: [
    { value: 'STRATEGIC_CONSULTING', label: 'Consulenza Strategica' },
  ]},
  { value: 'VISUAL_PRODUCTION', label: 'Visual Production', defaultType: 'VIDEO_SPOT', types: [
    { value: 'VIDEO_SPOT', label: 'Spot' },
    { value: 'VIDEO_CORPORATE', label: 'Video Corporate' },
    { value: 'VIDEO_DOCUFILM', label: 'Docufilm' },
    { value: 'VIDEO_REEL', label: 'Reel' },
    { value: 'VIDEO_INTERVIEW', label: 'Intervista' },
    { value: 'VIDEO_EVENT', label: 'Video Evento' },
    { value: 'PHOTO_SHOOTING', label: 'Shooting Fotografico' },
  ]},
  { value: 'MERCHANDISING', label: 'Merchandising', defaultType: 'CONTENT_CREATION', types: [
    { value: 'CONTENT_CREATION', label: 'Produzione Merchandising' },
  ]},
]

const STATUS_OPTIONS: { value: ProjectStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Bozza' },
  { value: 'PLANNING', label: 'Pianificazione' },
  { value: 'ACTIVE', label: 'Attivo' },
  { value: 'ON_HOLD', label: 'In Pausa' },
  { value: 'COMPLETED', label: 'Completato' },
  { value: 'CANCELLED', label: 'Annullato' },
]

const INITIAL_DATA: ProjectFormData = {
  name: '',
  clientId: '',
  isEvent: false,
  primaryModule: '',
  type: 'SOCIAL_MANAGEMENT',
  status: 'PLANNING',
  description: '',
  startDate: '',
  endDate: '',
  eventStartDate: '',
  eventEndDate: '',
  budget: '',
  managerId: '',
  location: '',
  isOnline: false,
  adPlatforms: [],
  adPlatformOther: '',
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

function FormField({
  label,
  required,
  children,
  error,
  hint,
}: {
  label: string
  required?: boolean
  children: React.ReactNode
  error?: string | undefined
  hint?: string | undefined
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1.5">
        {label}
        {required && <span className="text-danger-600 ml-0.5">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
      {error && <p className="text-xs text-danger-600 mt-1">{error}</p>}
    </div>
  )
}

const inputClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg',
  'focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20',
  'placeholder:text-slate-400 transition-colors'
)

const inputErrorClass = cn(
  'w-full px-3 py-2 text-sm bg-white border border-danger-300 rounded-lg',
  'focus:outline-none focus:border-danger-500 focus:ring-1 focus:ring-danger-500/20',
  'placeholder:text-slate-400 transition-colors'
)

// Helper to remove a key from errors without setting it to undefined
function clearError(errors: FormErrors, key: string): FormErrors {
  const next = { ...errors }
  delete next[key]
  return next
}

// ---------------------------------------------------------------------------
// ADV Platforms sub-section
// ---------------------------------------------------------------------------

function AdvPlatformsSection({
  formData,
  setFormData,
  handleChange,
  isPending,
}: {
  formData: ProjectFormData
  setFormData: React.Dispatch<React.SetStateAction<ProjectFormData>>
  handleChange: <K extends keyof ProjectFormData>(field: K, value: ProjectFormData[K]) => void
  isPending: boolean
}) {
  return (
    <FormField label="Piattaforme" hint="Su quali piattaforme gira la campagna?">
      <div className="grid grid-cols-2 gap-2 p-3 rounded-lg border border-slate-200 bg-slate-50">
        {AD_PLATFORMS.map((p) => {
          const checked = formData.adPlatforms.includes(p.value)
          return (
            <label
              key={p.value}
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm select-none',
                checked
                  ? 'bg-primary-50 border border-primary-300 text-primary-700 font-medium'
                  : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
              )}
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() => {
                  setFormData((prev) => ({
                    ...prev,
                    adPlatforms: checked
                      ? prev.adPlatforms.filter((v) => v !== p.value)
                      : [...prev.adPlatforms, p.value],
                  }))
                }}
                disabled={isPending}
                className="accent-primary-600 w-3.5 h-3.5"
              />
              <span className="truncate">{p.label}</span>
            </label>
          )
        })}
        {/* Piattaforme custom aggiunte */}
        {formData.adPlatformOther.split(',').filter(Boolean).map((tag) => (
          <label
            key={tag}
            className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm select-none bg-primary-50 border border-primary-300 text-primary-700 font-medium"
          >
            <input type="checkbox" checked readOnly className="accent-primary-600 w-3.5 h-3.5" />
            <span className="truncate flex-1">{tag}</span>
            <button
              type="button"
              onClick={(ev) => {
                ev.preventDefault()
                ev.stopPropagation()
                const tags = formData.adPlatformOther.split(',').filter(Boolean).filter((t) => t !== tag)
                handleChange('adPlatformOther', tags.join(','))
              }}
              className="ml-auto p-0.5 hover:text-danger-600 transition-colors"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </label>
        ))}
        {/* Input per aggiungere nuove piattaforme */}
        <div className="col-span-2">
          <input
            type="text"
            placeholder="Altra piattaforma + Invio"
            className={cn(inputClass, 'text-xs')}
            disabled={isPending}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                const val = (e.target as HTMLInputElement).value.trim()
                if (!val) return
                const existing = formData.adPlatformOther.split(',').filter(Boolean)
                if (!existing.includes(val)) {
                  handleChange('adPlatformOther', [...existing, val].join(','))
                }
                ;(e.target as HTMLInputElement).value = ''
              }
            }}
          />
        </div>
      </div>
    </FormField>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ProjectFormModal({ project, onClose, onSuccess }: ProjectFormModalProps) {
  const queryClient = useQueryClient()
  const isEdit = Boolean(project)

  const [formData, setFormData] = useState<ProjectFormData>(() => {
    if (project) {
      const projectIsEvent = EVENT_TYPES.includes(project.type)
      const mods = project.moduleTypes ?? []
      // Derive primary module: find which module owns the project type
      const ownerMod = MODULE_OPTIONS.find((o) => o.types.some((t) => t.value === project.type))
      const primary = ownerMod?.value ?? (mods[0] as ModuleType | undefined) ?? ''
      return {
        name: project.name,
        clientId: project.clientId ?? project.client?.id ?? '',
        isEvent: projectIsEvent,
        primaryModule: projectIsEvent ? '' : primary,
        type: project.type,
        status: project.status,
        description: project.description ?? '',
        startDate: project.startDate ? project.startDate.slice(0, 10) : '',
        endDate: project.endDate ? project.endDate.slice(0, 10) : '',
        eventStartDate: (project as any).eventStartDate ? (project as any).eventStartDate.slice(0, 10) : '',
        eventEndDate: (project as any).eventEndDate ? (project as any).eventEndDate.slice(0, 10) : '',
        budget: project.budget !== undefined ? String(project.budget) : '',
        managerId: project.managerId ?? project.manager?.id ?? '',
        location: project.location ?? '',
        isOnline: project.isOnline ?? false,
        adPlatforms: (project.adPlatforms ?? []).filter((p) => AD_PLATFORMS.some((ap) => ap.value === p)),
        adPlatformOther: (project.adPlatforms ?? []).filter((p) => !AD_PLATFORMS.some((ap) => ap.value === p)).join(', '),
      }
    }
    return INITIAL_DATA
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [successMsg, setSuccessMsg] = useState('')
  const [showClientForm, setShowClientForm] = useState(false)

  // Ogni progetto (creato a mano) deve derivare da una voce di preventivo
  // ACCETTATO: selezione a due passi, preventivo → voce non ancora collegata.
  // Non riguarda la modifica di un progetto esistente.
  const [selectedQuoteId, setSelectedQuoteId] = useState('')
  const [selectedLineItemId, setSelectedLineItemId] = useState('')

  // Fetch clients
  const { data: clientsResponse, isLoading: clientsLoading } = useQuery({
    queryKey: ['clients', { limit: 100 }],
    queryFn: () => clientsApi.list({ limit: 100 }),
  })
  const clients: Client[] = (() => {
    const raw = (clientsResponse as any)?.data
    if (Array.isArray(raw)) return raw
    if (raw?.items && Array.isArray(raw.items)) return raw.items
    if (raw?.data && Array.isArray(raw.data)) return raw.data
    return []
  })()

  // Fetch users (forDropdown=managers bypassa filtro dipartimento per L3)
  const { data: usersResponse, isLoading: usersLoading } = useQuery({
    queryKey: ['users', { limit: 100, forDropdown: 'managers' }],
    queryFn: () => usersApi.list({ limit: 100, forDropdown: 'managers' }),
  })
  const allUsers: User[] = (() => {
    const outer = usersResponse as any
    const raw = outer?.data ?? outer
    if (Array.isArray(raw)) return raw
    if (raw?.users && Array.isArray(raw.users)) return raw.users
    if (raw?.items && Array.isArray(raw.items)) return raw.items
    return []
  })()
  // Solo utenti con livello <= 3 (Manager+) possono essere responsabili
  const managers = allUsers.filter((u) => u.role?.level <= 3)

  // Preventivi accettati del cliente selezionato (solo in creazione)
  const { data: acceptedQuotesResponse, isLoading: quotesLoading } = useQuery({
    queryKey: ['quotes-accepted-for-project', formData.clientId],
    queryFn: () => quotesApi.list({ clientId: formData.clientId, status: 'ACCEPTED', limit: 100 }),
    enabled: !isEdit && Boolean(formData.clientId),
  })
  const acceptedQuotes: Quote[] = (() => {
    const raw = (acceptedQuotesResponse as any)?.data
    if (Array.isArray(raw)) return raw
    if (raw?.items && Array.isArray(raw.items)) return raw.items
    if (raw?.data?.items && Array.isArray(raw.data.items)) return raw.data.items
    return []
  })()

  // Voci del preventivo selezionato, filtrate a quelle non ancora collegate a un progetto
  const { data: selectedQuoteResponse, isLoading: quoteDetailLoading } = useQuery({
    queryKey: ['quote-detail-for-project', selectedQuoteId],
    queryFn: () => quotesApi.getById(selectedQuoteId),
    enabled: !isEdit && Boolean(selectedQuoteId),
  })
  const availableLineItems: QuoteLineItem[] = (() => {
    const raw = (selectedQuoteResponse as any)?.data
    const quote = (raw?.data ?? raw) as Quote | undefined
    return (quote?.lineItems ?? []).filter((li) => !li.projectId)
  })()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => projectsApi.create(data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      setSuccessMsg('Progetto creato con successo!')
      setTimeout(() => {
        onSuccess?.()
        onClose()
      }, 700)
    },
    onError: (err) => {
      setErrors((prev) => ({ ...prev, global: getApiErrorMessage(err) }))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      projectsApi.update(id, data),
    onSuccess: (result: any) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] })
      if (result?.pendingApproval) {
        setSuccessMsg(result.message || 'Modifica inviata per approvazione')
      } else {
        setSuccessMsg('Progetto aggiornato con successo!')
      }
      setTimeout(() => {
        onSuccess?.()
        onClose()
      }, 700)
    },
    onError: (err) => {
      setErrors((prev) => ({ ...prev, global: getApiErrorMessage(err) }))
    },
  })

  const isPending = createMutation.isPending || updateMutation.isPending

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onClose, isPending])

  // Computed: moduleTypes = solo il modulo principale
  const allModuleTypes: ModuleType[] = formData.primaryModule
    ? [formData.primaryModule]
    : []

  // Primary module config
  const primaryModuleConfig = MODULE_OPTIONS.find((o) => o.value === formData.primaryModule)

  function validate(): boolean {
    const newErrors: FormErrors = {}
    if (!formData.name.trim()) {
      newErrors.name = "Il nome del progetto e' obbligatorio"
    }
    if (!formData.clientId) {
      newErrors.clientId = 'Seleziona un cliente'
    }
    if (!isEdit && !selectedLineItemId) {
      newErrors.quoteLineItemId = 'Seleziona la voce di preventivo accettata da collegare al progetto'
    }
    if (!formData.isEvent && !formData.primaryModule) {
      newErrors.primaryModule = 'Seleziona un modulo principale'
    }
    if (!formData.status) {
      newErrors.status = 'Seleziona lo stato'
    }
    if (!formData.managerId) {
      newErrors.managerId = 'Seleziona un responsabile'
    }
    if (formData.budget && isNaN(parseFloat(formData.budget))) {
      newErrors.budget = 'Il budget deve essere un numero valido'
    }
    if (formData.endDate && formData.startDate && formData.endDate < formData.startDate) {
      newErrors.endDate = 'La data di fine deve essere successiva alla data di inizio'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  function handleChange<K extends keyof ProjectFormData>(field: K, value: ProjectFormData[K]) {
    setFormData((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) setErrors((prev) => clearError(prev, field))
    if (errors.global) setErrors((prev) => clearError(prev, 'global'))
  }

  function selectPrimaryModule(mod: ModuleType) {
    const config = MODULE_OPTIONS.find((o) => o.value === mod)
    setFormData((prev) => ({
      ...prev,
      primaryModule: mod,
      type: config?.defaultType ?? prev.type,
      // Reset ADV fields if switching away from ADV
      ...(mod !== 'ADV' && prev.primaryModule === 'ADV' ? { adPlatforms: [], adPlatformOther: '' } : {}),
    }))
    if (errors.primaryModule) setErrors((prev) => clearError(prev, 'primaryModule'))
    if (errors.global) setErrors((prev) => clearError(prev, 'global'))
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!validate()) return

    const moduleTypes = formData.isEvent ? ['EVENTI'] : allModuleTypes

    const payload: Record<string, unknown> = {
      name: formData.name.trim(),
      clientId: formData.clientId,
      type: formData.type,
      moduleTypes,
      status: formData.status,
      description: formData.description.trim() || undefined,
      startDate: formData.startDate || undefined,
      endDate: formData.endDate || undefined,
      eventStartDate: formData.eventStartDate || undefined,
      eventEndDate: formData.eventEndDate || undefined,
      budget: formData.budget ? parseFloat(formData.budget) : undefined,
      managerId: formData.managerId || undefined,
      ...(!isEdit ? { quoteLineItemId: selectedLineItemId } : {}),
      ...(formData.isEvent ? {
        location: formData.location.trim() || undefined,
        isOnline: formData.isOnline,
      } : {}),
      ...(() => {
        if (formData.primaryModule !== 'ADV') return {}
        const platforms = [...formData.adPlatforms]
        const other = formData.adPlatformOther.trim()
        if (other) {
          other.split(',').map((s) => s.trim()).filter(Boolean).forEach((s) => platforms.push(s))
        }
        return platforms.length > 0 ? { adPlatforms: platforms } : {}
      })(),
    }

    if (isEdit && project) {
      updateMutation.mutate({ id: project.id, data: payload })
    } else {
      createMutation.mutate(payload)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
        onClick={() => { if (!isPending) onClose() }}
      />

      {/* Modal panel */}
      <div className="relative bg-white rounded-3xl shadow-modal border border-slate-200 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 className="text-base font-semibold text-slate-900">
              {isEdit ? 'Modifica Progetto' : 'Nuovo Progetto'}
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {isEdit ? 'Aggiorna i dettagli del progetto' : 'Crea un nuovo progetto nel sistema'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => { if (!isPending) onClose() }}
            disabled={isPending}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="px-6 py-5 space-y-5">

            {/* Global error */}
            {errors.global && (
              <div className="p-3 bg-danger-50 border border-danger-200 rounded-lg">
                <span className="text-xs text-danger-700">{errors.global}</span>
              </div>
            )}

            {/* Success */}
            {successMsg && (
              <div className="p-3 bg-success-50 border border-success-200 rounded-lg">
                <span className="text-xs text-success-700">{successMsg}</span>
              </div>
            )}

            {/* Project name */}
            <FormField label="Nome Progetto" required error={errors.name}>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                placeholder="es. Social Media Q1 2026 - Cliente"
                className={errors.name ? inputErrorClass : inputClass}
                disabled={isPending}
                autoFocus
              />
            </FormField>

            {/* Cliente */}
            <FormField label="Cliente" required error={errors.clientId}>
              <div className="flex gap-1.5">
                <select
                  value={formData.clientId}
                  onChange={(e) => {
                    handleChange('clientId', e.target.value)
                    setSelectedQuoteId('')
                    setSelectedLineItemId('')
                  }}
                  className={cn(errors.clientId ? inputErrorClass : inputClass, 'flex-1')}
                  disabled={isPending || clientsLoading}
                >
                  <option value="">{clientsLoading ? 'Caricamento...' : 'Seleziona cliente'}</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowClientForm(true)}
                  disabled={isPending}
                  title="Crea nuovo cliente"
                  className={cn(
                    'flex items-center justify-center px-2.5 border border-slate-200 rounded-lg',
                    'text-slate-500 hover:bg-primary-50 hover:border-primary-400 hover:text-primary-600',
                    'transition-colors disabled:opacity-50 flex-shrink-0'
                  )}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </FormField>

            {/* Preventivo — ogni progetto deve derivare da una voce di preventivo accettata */}
            {!isEdit && (
              <FormField label="Preventivo (voce da collegare)" required error={errors.quoteLineItemId}>
                {!formData.clientId ? (
                  <p className="text-xs text-slate-400">Seleziona prima un cliente</p>
                ) : quotesLoading ? (
                  <p className="text-xs text-slate-400">Caricamento preventivi...</p>
                ) : acceptedQuotes.length === 0 ? (
                  <div className="p-3 bg-warning-50 border border-warning-200 rounded-lg text-xs text-warning-700">
                    Nessun preventivo accettato per questo cliente. Crea prima un preventivo e attendine l'accettazione: un progetto non può esistere senza un preventivo accettato collegato.
                  </div>
                ) : (
                  <div className="space-y-2">
                    <select
                      value={selectedQuoteId}
                      onChange={(e) => {
                        setSelectedQuoteId(e.target.value)
                        setSelectedLineItemId('')
                        if (errors.quoteLineItemId) setErrors((prev) => clearError(prev, 'quoteLineItemId'))
                      }}
                      className={inputClass}
                      disabled={isPending}
                    >
                      <option value="">Seleziona preventivo accettato...</option>
                      {acceptedQuotes.map((q) => (
                        <option key={q.id} value={q.id}>{q.quoteNumber} — {q.title}</option>
                      ))}
                    </select>
                    {selectedQuoteId && (
                      quoteDetailLoading ? (
                        <p className="text-xs text-slate-400">Caricamento voci...</p>
                      ) : availableLineItems.length === 0 ? (
                        <p className="text-xs text-warning-700">Tutte le voci di questo preventivo sono già collegate a un progetto.</p>
                      ) : (
                        <select
                          value={selectedLineItemId}
                          onChange={(e) => {
                            setSelectedLineItemId(e.target.value)
                            if (errors.quoteLineItemId) setErrors((prev) => clearError(prev, 'quoteLineItemId'))
                          }}
                          className={errors.quoteLineItemId ? inputErrorClass : inputClass}
                          disabled={isPending}
                        >
                          <option value="">Seleziona voce...</option>
                          {availableLineItems.map((li) => (
                            <option key={li.id} value={li.id}>{li.description} — {EUR.format(Number(li.lineTotal))}</option>
                          ))}
                        </select>
                      )
                    )}
                  </div>
                )}
              </FormField>
            )}

            {/* Toggle Evento */}
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">Categoria</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (!formData.isEvent) return
                    setFormData((prev) => ({ ...prev, isEvent: false, type: 'SOCIAL_MANAGEMENT', primaryModule: '', location: '', isOnline: false }))
                  }}
                  disabled={isPending}
                  className={cn(
                    'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all',
                    !formData.isEvent
                      ? 'bg-primary-600 border-primary-600 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                  )}
                >
                  Progetto
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (formData.isEvent) return
                    setFormData((prev) => ({ ...prev, isEvent: true, type: 'EVENT_ORGANIZATION', primaryModule: '' }))
                  }}
                  disabled={isPending}
                  className={cn(
                    'flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all',
                    formData.isEvent
                      ? 'bg-warning-600 border-warning-600 text-white'
                      : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                  )}
                >
                  <CalendarDays className="w-4 h-4" />
                  Evento
                </button>
              </div>
            </div>

            {/* ── Sezione Evento ─────────────────────────────── */}
            {formData.isEvent && (
              <div className="p-4 bg-warning-50 border border-warning-200 rounded-2xl space-y-4">
                <FormField label="Tipo Evento" required>
                  <select
                    value={formData.type}
                    onChange={(e) => handleChange('type', e.target.value as ProjectType)}
                    className={inputClass}
                    disabled={isPending}
                  >
                    {EVENT_TYPE_OPTIONS.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </FormField>
                <div className="grid grid-cols-2 gap-4">
                  <FormField label="Location">
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleChange('location', e.target.value)}
                      placeholder="es. Milano, Fiera Rho"
                      className={inputClass}
                      disabled={isPending}
                    />
                  </FormField>
                  <FormField label="Modalita'">
                    <label
                      className={cn(
                        'flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-all text-sm',
                        formData.isOnline
                          ? 'bg-primary-50 border-primary-300 text-primary-700'
                          : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 hover:border-slate-300'
                      )}
                    >
                      <input
                        type="checkbox"
                        checked={formData.isOnline}
                        onChange={(e) => handleChange('isOnline', e.target.checked)}
                        disabled={isPending}
                        className="accent-primary-600 w-3.5 h-3.5"
                      />
                      <Globe className="w-3.5 h-3.5" />
                      Evento Online
                    </label>
                  </FormField>
                </div>
              </div>
            )}

            {/* ── Sezione Progetto (non-evento) ──────────────── */}
            {!formData.isEvent && (
              <>
                {/* 1. Modulo Principale — radio */}
                <FormField label="Modulo Principale" required error={errors.primaryModule}>
                  <div className={cn(
                    'grid grid-cols-2 gap-2 p-3 rounded-lg border',
                    errors.primaryModule ? 'border-danger-300 bg-danger-50' : 'border-slate-200 bg-slate-50'
                  )}>
                    {MODULE_OPTIONS.map((opt) => {
                      const selected = formData.primaryModule === opt.value
                      return (
                        <label
                          key={opt.value}
                          className={cn(
                            'flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all text-sm select-none',
                            selected
                              ? 'bg-primary-50 border border-primary-300 text-primary-700 font-medium'
                              : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'
                          )}
                        >
                          <input
                            type="radio"
                            name="primaryModule"
                            checked={selected}
                            onChange={() => selectPrimaryModule(opt.value)}
                            disabled={isPending}
                            className="accent-primary-600 w-3.5 h-3.5"
                          />
                          <span className="truncate">{opt.label}</span>
                        </label>
                      )
                    })}
                  </div>
                </FormField>

                {/* 2. Tipo Progetto — dropdown solo se il modulo ha più sotto-tipi */}
                {primaryModuleConfig && primaryModuleConfig.types.length > 1 && (
                  <FormField label="Tipo Progetto" required>
                    <select
                      value={formData.type}
                      onChange={(e) => handleChange('type', e.target.value as ProjectType)}
                      className={inputClass}
                      disabled={isPending}
                    >
                      {primaryModuleConfig.types.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </FormField>
                )}

                {/* 3. Piattaforme ADV — solo se modulo principale è ADV */}
                {formData.primaryModule === 'ADV' && (
                  <AdvPlatformsSection formData={formData} setFormData={setFormData} handleChange={handleChange} isPending={isPending} />
                )}

              </>
            )}

            {/* Status + Manager */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Stato" required error={errors.status}>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value as ProjectStatus)}
                  className={errors.status ? inputErrorClass : inputClass}
                  disabled={isPending}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </FormField>

              <FormField label="Responsabile" required error={errors.managerId}>
                <select
                  value={formData.managerId}
                  onChange={(e) => handleChange('managerId', e.target.value)}
                  className={errors.managerId ? inputErrorClass : inputClass}
                  disabled={isPending || usersLoading}
                >
                  <option value="">{usersLoading ? 'Caricamento...' : 'Seleziona responsabile'}</option>
                  {managers.map((u) => (
                    <option key={u.id} value={u.id}>{u.firstName} {u.lastName} — {u.role?.name}</option>
                  ))}
                </select>
              </FormField>
            </div>

            {/* Description */}
            <FormField label="Descrizione" hint="Breve descrizione degli obiettivi del progetto">
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Descrivi gli obiettivi e il contesto del progetto..."
                rows={3}
                className={cn(inputClass, 'resize-none')}
                disabled={isPending}
              />
            </FormField>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Data Inizio">
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleChange('startDate', e.target.value)}
                  className={inputClass}
                  disabled={isPending}
                />
              </FormField>

              <FormField label="Data Fine" error={errors.endDate}>
                <input
                  type="date"
                  value={formData.endDate}
                  min={formData.startDate || undefined}
                  onChange={(e) => handleChange('endDate', e.target.value)}
                  className={errors.endDate ? inputErrorClass : inputClass}
                  disabled={isPending}
                />
              </FormField>
            </div>

            {/* Event Dates */}
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Data Inizio Evento" hint="Opzionale">
                <input
                  type="date"
                  value={formData.eventStartDate}
                  onChange={(e) => handleChange('eventStartDate', e.target.value)}
                  className={inputClass}
                  disabled={isPending}
                />
              </FormField>

              <FormField label="Data Fine Evento" hint="Opzionale">
                <input
                  type="date"
                  value={formData.eventEndDate}
                  min={formData.eventStartDate || undefined}
                  onChange={(e) => handleChange('eventEndDate', e.target.value)}
                  className={inputClass}
                  disabled={isPending}
                />
              </FormField>
            </div>

            {/* Budget */}
            <FormField label="Budget (€)" error={errors.budget} hint="Budget totale del progetto in euro">
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400">€</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.budget}
                  onChange={(e) => handleChange('budget', e.target.value)}
                  placeholder="0.00"
                  className={cn(errors.budget ? inputErrorClass : inputClass, 'pl-7 tabular-nums')}
                  disabled={isPending}
                />
              </div>
            </FormField>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200 bg-slate-50 flex-shrink-0">
            <p className="text-xs text-slate-400">
              I campi con <span className="text-danger-600">*</span> sono obbligatori
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => { if (!isPending) onClose() }}
                disabled={isPending}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="submit"
                disabled={isPending}
                className={cn(
                  'flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg',
                  'hover:bg-primary-700 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed'
                )}
              >
                {isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {isPending
                  ? (isEdit ? 'Salvataggio...' : 'Creazione...')
                  : (isEdit ? 'Salva Modifiche' : 'Crea Progetto')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Sub-modal: Crea nuovo cliente */}
      {showClientForm && (
        <ClientForm
          compact
          onClose={() => setShowClientForm(false)}
          onSuccess={(newClient) => {
            handleChange('clientId', newClient.id)
            setShowClientForm(false)
          }}
        />
      )}
    </div>
  )
}
